import { toHex } from './hover-matching';
import { DataAttrConfigRef, DataAttrHints } from 'types/apis/cxEditor';

// Block tags that form distinct content regions. When extracting data-attribute
// hints, we stop walking ancestors at the first block element that carries no
// data-attrs — the hover target belongs to a different region from any
// data-attrs that live on a more-distant ancestor.
// TD and TH are omitted intentionally: data-attrs are sometimes placed on the
// parent TR rather than the cell, so we allow traversal past cell boundaries
// into the row. TABLE and TR remain as stops to prevent cross-row leakage.
const DATA_ATTR_BLOCK_STOP = new Set([
  'DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'LI', 'SECTION', 'ARTICLE', 'TABLE', 'TR', 'HEADER', 'FOOTER', 'MAIN',
]);

// Realm-safe element check: `instanceof Element` fails for nodes from a
// different realm (e.g. inside an iframe). Duck-type via `nodeType` instead.
function asElement(target: EventTarget | null): Element | null {
  if (!target) return null;
  const anyTarget = target as { nodeType?: number; parentElement?: Element | null };
  if (anyTarget.nodeType === 1) return target as Element;
  if (typeof anyTarget.nodeType === 'number') return anyTarget.parentElement ?? null;
  return null;
}

export function getEventPathFirstElement(event: MouseEvent, win: Window, container: Element): Element | null {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
  for (const node of path) {
    const el = asElement(node);
    if (el && isWithinBoundary(el, container)) {
      return el;
    }
  }
  const fallback = asElement(event.target);
  return fallback && isWithinBoundary(fallback, container) ? fallback : null;
}

export function getDeepestElementFromPoint(doc: Document, x: number, y: number): Element | null {
  let el = doc.elementFromPoint(x, y);
  while (el && el instanceof HTMLElement && el.shadowRoot) {
    const nested = el.shadowRoot.elementFromPoint(x, y);
    if (!nested || nested === el) break;
    el = nested;
  }
  return el;
}

const POPUP_NAME_RE = /(?:^|[-_\s])(tooltip|popup|popover|hovercard|flyout|overlay)(?:$|[-_\s])/i;
const POPUP_ROLES = new Set(['tooltip', 'menu', 'dialog']);

function elementLooksLikePopup(el: Element): boolean {
  const role = el.getAttribute('role') || '';
  if (POPUP_ROLES.has(role)) return true;
  const cls = typeof (el as HTMLElement).className === 'string' ? (el as HTMLElement).className : '';
  const id = el.id || '';
  return POPUP_NAME_RE.test(cls) || POPUP_NAME_RE.test(id);
}

export function isPopupLike(el: Element | null, stopAt: Element): boolean {
  let node: Element | null = el;
  let depth = 0;
  while (node && node !== stopAt && depth < 10) {
    if (elementLooksLikePopup(node)) return true;
    node = node.parentElement;
    depth += 1;
  }
  return false;
}

/**
 * Hit-test that skips transient popup/tooltip overlays, returning the
 * content element underneath instead.
 */
export function getNonPopupElementFromPoint(
  doc: Document,
  x: number,
  y: number,
  boundary: Element,
): Element | null {
  const top = getDeepestElementFromPoint(doc, x, y);
  if (!top) return null;
  if (!isPopupLike(top, boundary)) return top;
  const stack = typeof doc.elementsFromPoint === 'function'
    ? doc.elementsFromPoint(x, y)
    : [];
  for (const el of stack) {
    if (!isWithinBoundary(el, boundary)) continue;
    if (isPopupLike(el, boundary)) continue;
    return el;
  }
  return top;
}

// Shadow-DOM-aware containment check.
export function isWithinBoundary(el: Element | null, boundary: Element): boolean {
  let node: Node | null = el;
  while (node) {
    if (node === boundary) return true;
    const parent: Node | null = node.parentNode;
    if (parent && typeof (parent as ShadowRoot).host !== 'undefined' && (parent as ShadowRoot).host) {
      node = (parent as ShadowRoot).host;
    } else {
      node = parent;
    }
  }
  return false;
}

export function getElementTextColorHex(el: Element, win: Window): string | null {
  const style = win.getComputedStyle(el);
  return toHex(style.color) || toHex(style.fill || '') || toHex(style.stroke || '');
}

export function resolveElementBackgroundHex(el: Element, stopAt: Element, win: Window): string | null {
  let node: Element | null = el;
  while (node) {
    const hex = toHex(win.getComputedStyle(node).backgroundColor || '');
    if (hex) return hex;
    if (node === stopAt) break;
    node = node.parentElement;
  }
  return null;
}

/**
 * Walk up from `el` to find the nearest ancestor carrying data-configs /
 * data-strings / data-colors, parse them into DataAttrHints for direct
 * O(1) inspector field lookup. Returns null when no data attributes found.
 */
export function extractDataAttrHints(el: Element, stopAt: Element): DataAttrHints | null {
  let node: Element | null = el;
  while (node && node !== stopAt) {
    if (
      node.hasAttribute('data-configs') ||
      node.hasAttribute('data-strings') ||
      node.hasAttribute('data-colors')
    ) break;
    if (DATA_ATTR_BLOCK_STOP.has(node.tagName)) return null;
    node = node.parentElement;
  }
  if (!node || node === stopAt) return null;

  const textIds: string[] = [];
  const colorIds: string[] = [];
  try {
    const raw = node.getAttribute('data-configs');
    if (raw) {
      for (const c of JSON.parse(raw) as DataAttrConfigRef[]) {
        if (c.type?.includes('palette')) colorIds.push(c.key);
        else textIds.push(c.key);
      }
    }
  } catch { /* malformed JSON — skip */ }

  const stringIds: string[] = [];
  try {
    const raw = node.getAttribute('data-strings');
    if (raw) {
      for (const s of JSON.parse(raw) as string[]) {
        if (typeof s === 'string' && !s.startsWith('[')) stringIds.push(s);
      }
    }
  } catch { /* malformed JSON — skip */ }

  const colorsAttr = node.getAttribute('data-colors');
  if (colorsAttr) {
    for (const k of colorsAttr.split(',').map(s => s.trim()).filter(Boolean)) {
      if (!colorIds.includes(k)) colorIds.push(k);
    }
  }

  if (!textIds.length && !stringIds.length && !colorIds.length) return null;
  return { textIds, stringIds, colorIds };
}
